// Design-system reminder: every title uses a typography.h* preset, OR `font-heading font-bold`.
//
// Single source of the published Affiliate Program Terms text. Rendered both in
// the affiliate portal acceptance gate and on the public /affiliate-terms page,
// so an affiliate reads exactly the text they accept.
//
// When the text changes materially, bump AFFILIATE_TERMS_VERSION on the backend
// (portfolio_creator/portfolio_creator/settings.py) and the constants below, so
// affiliates are asked to accept the new version and the acceptance history
// records which text was in force.
import React from "react";
import { typography, textColors } from "../../design-system/tokens";

export const AFFILIATE_TERMS_DOCUMENT_ID = "AFFILIATE-PROGRAM-TERMS-2026-08-09";
export const AFFILIATE_TERMS_VERSION = "2026-08-09";
export const AFFILIATE_TERMS_LAST_UPDATED = "August 9, 2026";

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className={`${typography.h4} ${textColors.primary}`}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className={`${typography.bodySmall} ${textColors.secondary} leading-relaxed`}>{children}</p>
);

const List = ({ items }) => (
  <ul className={`list-disc pl-5 space-y-1 ${typography.bodySmall} ${textColors.secondary}`}>
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const AffiliateTermsContent = () => (
  <div className="space-y-6">
    <P>
      These Affiliate Program Terms of Use ("Affiliate Terms") govern your participation in the
      myvitae.ai affiliate program (the "Program"), operated by Vitae AI LLC ("Company", "we", "us").
      They are a separate agreement from, and in addition to, the myvitae.ai Terms of Use and Privacy
      Policy, which continue to apply to your use of the service. Document {AFFILIATE_TERMS_DOCUMENT_ID},
      version {AFFILIATE_TERMS_VERSION}.
    </P>

    <Section title="1. Acceptance and enrollment">
      <P>
        You must accept these Affiliate Terms before you can use the affiliate portal or earn any
        commission. We record your acceptance electronically, including the version, date and time,
        IP address, and browser, and we keep a history of every version you have accepted.
      </P>
      <P>
        If you accept on behalf of a company, you confirm you have authority to bind it. Participation
        is at our discretion, and being a myvitae.ai user does not entitle you to affiliate status.
        Where these Affiliate Terms conflict with the general Terms of Use on Program matters, these
        Affiliate Terms control.
      </P>
    </Section>

    <Section title="2. Eligibility">
      <P>
        You must be at least 18 years old and legally able to enter into contracts. You must not be
        located in, or a resident of, a jurisdiction subject to comprehensive sanctions, and you must
        not appear on an applicable restricted-party list. You must keep one affiliate account unless
        we agree otherwise in writing, keep your contact and payout details accurate, and safeguard
        your credentials — you are responsible for activity under your account.
      </P>
    </Section>

    <Section title="3. Independent contractor status">
      <P>
        You are an independent contractor. Nothing here creates an employment, partnership, joint
        venture, franchise, or agency relationship, and you are not entitled to employee benefits.
        You may not make representations or commitments on our behalf, or present yourself as our
        employee, agent, reseller, support channel, or spokesperson. You bear your own marketing costs.
      </P>
    </Section>

    <Section title="4. Referral tracking and attribution">
      <P>
        Referrals are tracked only through the coupon codes and discount links we issue to you. A user
        becomes linked to you when they register with your coupon, or when they make their first paid
        purchase with your coupon.
      </P>
      <P>
        <strong>The first link wins and is permanent.</strong> Once a user is linked to an affiliate,
        the link is never reassigned, and later clicks or codes from any affiliate have no effect.
        There is no expiring cookie window. After a user is linked to you, their later successful paid
        invoices count for you even if your coupon is no longer applied. Invoices of $0 — free trials,
        fully discounted invoices, and free-access grants — earn no commission.
      </P>
      <P>
        Our tracking, ledger, and commission records are the authoritative record of referrals,
        amounts, and statuses, absent manifest error. Please raise any discrepancy in writing within 60
        days of the relevant calendar month. To protect user privacy, the portal never discloses the
        identity or personal data of referred users, and you may not attempt to identify, contact, or
        re-target them using Company data.
      </P>
    </Section>

    <Section title="5. Commission">
      <P>
        Commission is a share of amounts your referred users actually pay on successful invoices
        (amount paid after discounts), in U.S. dollars. Your rate for a calendar month depends on how
        many new paying referrals are attributed to you in that month — distinct referred users whose
        first commissionable payment falls in that month:
      </P>
      <List
        items={[
          "0–4 new paying referrals: 20%",
          "5–9 new paying referrals: 25%",
          "10–19 new paying referrals: 30%",
          "20 or more new paying referrals: 40%",
        ]}
      />
      <P>
        That rate applies to all commissionable payments credited to you in the same calendar month,
        not only to payments from the new referrals. During an open month, amounts shown are estimates
        and may change.
      </P>
      <P>
        <strong>We may change the commission schedule</strong> — rates, tiers, qualifying events, or
        structure — on 14 days' notice under section 11. Changes apply going forward from the effective
        date and do not reduce commission already accrued for months that closed before it. No
        commission is payable on taxes, refunded or charged-back amounts, or amounts we determine were
        generated in breach of these Affiliate Terms. We do not guarantee any level of earnings.
      </P>
    </Section>

    <Section title="6. Monthly periods, hold, approval, and payment">
      <P>
        Commission accrues in monthly periods — one per affiliate per calendar month with activity.
        When a month closes, the amount is recalculated once, frozen, and held for 14 days. If your
        payout account is not ready when the month closes, the period waits for payout setup and the
        hold clock pauses until your account is ready.
      </P>
      <P>
        After the hold, the period awaits our review. <strong>All payouts require Company approval</strong>;
        payouts are not automatic, and we may approve a period before the hold has fully elapsed.
        Approved periods are paid by transfer to your connected payout account. We may reject a period,
        in which case you will see our note in the portal.
      </P>
      <P>
        There is currently no minimum payout threshold, so any positive frozen period can be paid once
        your payout account is ready. "Amount owed" in the portal means frozen, unpaid periods; it
        excludes the current month's live estimate. We may withhold or suspend payment while we
        investigate suspected fraud, prohibited conduct, chargeback abuse, or a breach of these
        Affiliate Terms, and we may set off amounts you owe us.
      </P>
    </Section>

    <Section title="7. Payout account and Stripe">
      <P>
        Payouts are made through Stripe using a Stripe Express connected account. You must complete
        Stripe onboarding and keep your account payout-ready to be paid.{" "}
        <strong>
          By accepting these Affiliate Terms and creating a connected account, you also agree to the
          applicable Stripe connected account agreement
        </strong>{" "}
        (see the{" "}
        <a
          href="https://stripe.com/legal/connect-account"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Stripe Connected Account Agreement
        </a>{" "}
        and the{" "}
        <a
          href="https://stripe.com/connect-account/legal/recipient"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Stripe Recipient Agreement
        </a>
        ), and you must give Stripe accurate, current information.
      </P>
      <P>
        We are not responsible for delays, holds, reversals, currency conversion, or fees applied by
        Stripe, your bank, or intermediaries, or for failures caused by incorrect payout details. We may
        change payment providers or payout methods on notice.
      </P>
    </Section>

    <Section title="8. Taxes">
      <P>
        You are solely responsible for determining, reporting, and paying all taxes on commissions you
        receive. Tax and identity information for payouts is collected through Stripe's onboarding, and
        you must complete any documentation Stripe requires. We may withhold payment where documentation
        is missing or withholding is required by law.
      </P>
    </Section>

    <Section title="9. Your obligations and prohibited conduct">
      <P>
        You must comply with all applicable laws in your marketing, including advertising,
        consumer-protection, privacy, email, and messaging laws.
      </P>
      <P>
        <strong>Disclosure is mandatory.</strong> You must clearly and conspicuously disclose your
        affiliate relationship in every endorsement, post, video, email, or page that contains a Program
        link or code, consistent with the FTC Endorsement Guides (16 CFR Part 255), and{" "}
        <strong>you must use the disclosure wording we approve or provide</strong>. Substituting
        different wording, burying the disclosure, or placing it after the endorsement is a material
        breach.
      </P>
      <P>
        You must not create or spread fake, fabricated, or AI-fabricated reviews or testimonials about
        us, including content misrepresenting that a reviewer exists or used the service (16 CFR Part
        465). You must not make false, misleading, or unsupported claims about the service — in
        particular, you must never promise or imply that using myvitae.ai will result in employment, job
        offers, interviews, university or program admission, scholarships, grants, visas, immigration
        outcomes, publication, or any specific professional or academic result.
      </P>
      <P>
        <strong>Paid search:</strong> you must not bid on, or use in ad copy, display URLs, or
        landing-page domains, our names, marks, or misspellings (including "myvitae", "my vitae",
        "myvitae.ai", and "Vitae AI") without our prior written approval. Where we approve, you must
        follow our conditions, and we may withdraw approval on notice.
      </P>
      <P>You must not:</P>
      <List
        items={[
          "engage in cookie stuffing, forced clicks, or iframe or toolbar injection, or any technique that records tracking without a genuine user action;",
          "use bots, click farms, automated scripts, or purchased or artificially generated traffic;",
          "send spam, or messages that fail applicable opt-out and sender-identification rules;",
          "register or use domains, social handles, app names, or business names containing our marks or confusingly similar variants;",
          "present yourself as the Company, its support, or an official reseller, or run lookalike sites;",
          "publish false, expired, altered, or unauthorized discount codes, or misstate any offer's terms, price, or availability;",
          "create or use additional or fictitious accounts, or coordinated multi-account activity, to generate or inflate commissions;",
          "promote the service alongside illegal, hateful, harassing, sexually explicit, defamatory, or infringing content;",
          "alter our creatives so their meaning changes, or strip required disclosures from them;",
          "interfere with or attempt to manipulate our tracking, coupon, payment, or portal systems.",
        ]}
      />
      <P>
        <strong>Using your own coupon for your own account is allowed.</strong> You may use your coupon
        on your own myvitae.ai subscription and earn commission on it. This does not permit fictitious or
        multi-account activity, unauthorized payment methods, or commission on payments that are later
        refunded or reversed.
      </P>
      <P>
        On request, you must provide within 5 business days copies or URLs of your Program content,
        disclosures, campaigns, and traffic sources, and you must keep records of your Program marketing
        for 2 years. If you breach this section, we may void affected commissions, withhold or reverse
        unpaid amounts, suspend your account during investigation, remove your coupons, and terminate
        your participation for cause.
      </P>
    </Section>

    <Section title="10. Trademarks, brand assets, and content">
      <P>
        We grant you a limited, revocable, non-exclusive, non-transferable, non-sublicensable licence to
        use the brand assets and creatives we provide, solely to promote the service while you
        participate, and only in the form provided or approved. We reserve all other rights; you gain no
        ownership or goodwill in our marks and must not seek to register them. We may require you to
        change or remove Program content we consider inaccurate, non-compliant, or damaging to our
        reputation. On termination, the licence ends and you must remove our marks, creatives, coupons,
        and Program links within 7 days. You are responsible for your own channels and content and must
        hold the rights to any third-party material you publish.
      </P>
    </Section>

    <Section title="11. Changes to these Affiliate Terms and the Program">
      <P>
        We may amend these Affiliate Terms and the commission schedule by publishing an updated version
        with a new version identifier and notifying you by email and/or in the portal at least 14 days
        before the effective date. Changes apply going forward and do not affect commission already
        accrued in months that closed before the effective date.
      </P>
      <P>
        Accepting the new version in the portal, or continuing to participate on or after the effective
        date, means you accept it. If you do not accept a change, you may leave the Program before the
        effective date, and periods that closed earlier are handled under the prior version. We keep your
        acceptance history and prior versions, available on request. We may modify, suspend, or
        discontinue the Program or any coupon, link, feature, or payout method at any time; if we
        discontinue the Program, section 12 applies as if we had terminated for convenience.
      </P>
    </Section>

    <Section title="12. Term, termination, and effect">
      <P>
        These Affiliate Terms apply from your acceptance until participation ends. You may leave the
        Program at any time by written notice. We may terminate or suspend your participation for
        convenience on notice, and immediately for cause — including fraud, breach of section 9, misuse of
        our marks, legal-compliance concerns, or conduct that harms the service or our reputation.
      </P>
      <P>
        <strong>On termination for any reason, commission stops accruing immediately.</strong> You earn
        nothing on payments made by referred users after termination takes effect, and no continuing
        revenue share survives the relationship.
      </P>
      <P>
        On termination for convenience or by you, periods that closed before termination — and the period
        covering the month of termination, calculated to the effective date — are processed as usual
        (hold, Company approval, payment) and paid if approved, provided your payout account is ready and
        no breach is under investigation. On termination for cause, commissions attributable to the breach
        are forfeited and we may withhold, reduce, or refuse affected unpaid amounts; unaffected accrued
        amounts are paid normally.
      </P>
    </Section>

    <Section title="13. Refunds, reversals, and adjustments">
      <P>
        Commission is earned only on payments we actually keep. If an underlying payment is refunded,
        disputed, charged back, reversed, or excluded, we may reduce or cancel the affected commission
        before it is paid: a partial reduction leaves the period pending, and a reduction to zero voids
        it. We do not currently claw back commission already paid to you, but we reserve the right to
        recover amounts paid in cases of fraud or breach of section 9 and to set them off against future
        commission.
      </P>
    </Section>

    <Section title="14. Confidentiality and data protection">
      <P>
        Non-public Program information — including negotiated rates, unreleased features, Program metrics,
        and anything marked or reasonably understood as confidential — must not be disclosed or used
        outside the Program. Each party is an independent controller of the personal data it processes for
        its own purposes, and you are responsible for the lawfulness of your own marketing lists,
        consents, and privacy notices. You must not use Company systems to obtain personal data about
        users of the service, and you must tell us promptly about any security incident affecting your
        Program credentials, coupons, or payout details.
      </P>
    </Section>

    <Section title="15. Indemnification">
      <P>
        You will indemnify, defend, and hold harmless the Company and its members, managers, officers,
        employees, and agents against claims, investigations, fines, penalties, losses, damages,
        liabilities, and reasonable legal fees arising from: your marketing, content, statements, or
        omissions, including any failure to disclose your affiliate relationship or any false or
        fabricated review; your breach of these Affiliate Terms or of law; your use or misuse of our
        marks or creatives; any claim that you are our employee or agent; and your handling of personal
        data. We may participate in the defense with counsel of our choosing, and you may not settle in a
        way that imposes obligations on us without our written consent.
      </P>
    </Section>

    <Section title="16. Disclaimers and limitation of liability">
      <P>
        The Program and affiliate portal are provided "as is" and "as available", without warranties of
        any kind, including merchantability, fitness for a particular purpose, non-infringement,
        uninterrupted tracking, or the accuracy of reporting. We do not warrant that tracking captures
        every referral, and we are not responsible for referrals lost through incorrect links or codes,
        privacy settings, ad blockers, browser behavior, or third-party platform changes.
      </P>
      <P>
        To the fullest extent permitted by law, neither party is liable for indirect, incidental, special,
        consequential, exemplary, or punitive damages, or for lost profits, revenue, goodwill, or
        anticipated commissions. Our total aggregate liability relating to the Program will not exceed the
        total commissions actually paid to you in the 12 months before the event giving rise to the claim,
        or US$100 if no commissions were paid. That cap does not limit your indemnification obligations,
        liability for fraud or wilful misconduct, or liability for infringement or misuse of our
        intellectual property.
      </P>
    </Section>

    <Section title="17. Dispute resolution and governing law">
      <P>
        These Affiliate Terms are governed by the laws of the State of Delaware and the United States,
        without regard to conflict-of-law principles. Before starting formal proceedings, you agree to
        contact us at legal@myvitae.ai with a written description of the dispute and the relief sought,
        and to try in good faith to resolve it for 30 days.
      </P>
      <P>
        Except as stated below, disputes relating to the Program will be resolved by final and binding
        individual arbitration administered by the American Arbitration Association under its Commercial
        Arbitration Rules, in English, seated in Delaware. TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AND
        THE COMPANY EACH WAIVE ANY RIGHT TO A JURY TRIAL AND ANY RIGHT TO BRING OR PARTICIPATE IN A CLASS,
        COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. Arbitration does not apply to claims
        eligible for small claims court, claims for injunctive or equitable relief about intellectual
        property, unauthorized access, or confidential information, or claims where mandatory arbitration
        is prohibited by law. Each party bears its own costs unless the arbitrator finds a claim frivolous
        or brought in bad faith.
      </P>
    </Section>

    <Section title="18. General">
      <P>
        These Affiliate Terms, with any Program agreement we sign with you, are the entire agreement about
        the Program; a signed agreement controls for that affiliate where it conflicts. We may give notice
        by email to your account address or in the portal, and you consent to electronic Program notices;
        notices to us go to legal@myvitae.ai. You may not assign your participation, coupons, or accrued
        commission without our written consent, and we may assign these Affiliate Terms in a merger,
        acquisition, financing, or sale of assets. If a provision is unenforceable, the rest stays in
        effect and that provision is limited to the minimum necessary; no delay in enforcing a right waives
        it. Neither party is liable for delays caused by events beyond its reasonable control, except for
        amounts already due. These Affiliate Terms confer no rights on third parties, except that Stripe's
        rights under its own agreements are unaffected. Headings are for convenience, and "including" means
        "including without limitation".
      </P>
      <P>Questions: legal@myvitae.ai — Vitae AI LLC, operator of myvitae.ai.</P>
    </Section>
  </div>
);

export default AffiliateTermsContent;
